import { namespace } from 'wsm-tagging-utils';

const runEventHandler = (win, flags) => {
	namespace(win, 'DDC.tracking.framework.runtime');

	const {
		DDC: { tracking },
		com
	} = win;

	/**
	 * The EventHandler defines the interface operations expected by the InteractionListener. All
	 * implementations should inherit from this EventHandler and provide the necessary behavior by
	 * overriding the appropriate methods and providing the implementation.
	 *
	 * @param {Object} window An instance of the global window object; allows unit tests to inject a mock
	 *               for testing
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @param {tracking.framework.runtime.Form} form A Form instance for form related queries
	 * @constructor
	 *
	 * @see https://sourcemaking.com/design_patterns/strategy
	 */
	tracking.framework.runtime.EventHandler = function EventHandler(
		thisWin,
		page,
		form
	) {
		this.log = com.dealer.log.Logger.createFor(this);
		this.win = thisWin;
		this.page = page;
		this.form = form;
		this.widgetId = undefined;
		this.flags = flags || {};
	};

	tracking.framework.runtime.EventHandler.prototype = {
		/**
		 * The default constructor
		 */
		constructor: win.DDC.tracking.framework.runtime.EventHandler,

		/**
		 * The logging category, subclasses should override to appropriate value
		 */
		logCategory: 'ws-tagging:EventHandler',

		flags: flags || {},

		//= ===================================================================
		// The following operations are triggered when events are pushed
		// onto the data layer via window.dataLayer.push({event:'name', ...})
		//= ===================================================================

		/**
		 * Handles the page view event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handlePageView(_eventData) {},

		/**
		 * Handles the vehicle detail view event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleVehicleDetailView(_eventData) {},

		/**
		 * Handles the slideshow click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleSlideshowClick(_eventData) {},

		/**
		 * Handles the ajax content loaded event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleAjaxContentLoad(_eventData) {},

		/**
		 * Handles the dialog open event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleDialogOpened(_eventData) {},

		/**
		 * Handles the dialog close event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleDialogClosed(_eventData) {},

		/**
		 * Handles the form change event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleFormChange(_eventData) {},

		/**
		 * Handles the typed search event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleTypedSearch(_eventData) {},

		/**
		 * Handles the typed search initiation event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleTypedSearchInit(_eventData) {},

		/**
		 * Handles the typed search results event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleTypedSearchResults(_eventData) {},

		/**
		 * Handles the payment calculator form initiation event.
		 *
		 * @param {Object} eventData The event data object.
		 */
		handlePaymentCalcInit(_eventData) {},

		/**
		 * Handles the form confirmation event.
		 *
		 * @param {Object} eventData The event data object.
		 */
		handleFormConfirmation(_eventData) {},

		/**
		 * Handles the form initiation event.
		 *
		 * @param {Object} eventData The event data object.
		 */
		handleFormInitiation(_eventData) {},

		/**
		 * Handles the form onPage event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleFormOnPage(_eventData) {},

		/**
		 * Handles the form submission event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleFormSubmission(_eventData) {},

		/**
		 * Handles the click to call event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleClickToCall(_eventData) {},

		/**
		 * Handles the compare page click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleComparePageClick(_eventData) {},

		/**
		 * Handles the hproduct click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleHproductClick(_eventData) {},

		/**
		 * Handles the recommendations vehicles link click.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleRecommendedVehicleClick(_eventData) {},

		/**
		 * Handles the offer click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleOfferClick(_eventData) {},

		/**
		 * Handles the navigation click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleNavigationClick(_eventData) {},

		/**
		 * Handles the filter search event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleFilterSearch(_eventData) {},

		/**
		 * Handles the filter search click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleFilterSearchClick(_eventData) {},

		/**
		 * Handles the incentive filter event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleIncentiveFilter(_eventData) {},

		/**
		 * Handles link tracking (e.g. buttons, anchors)
		 *
		 * @param {Object} eventData The event data object
		 */
		handleLinkClick(_eventData) {},

		/**
		 * Handles the map click event.
		 */
		handleMapClick(_eventData) {},

		/**
		 * Handles the social header click event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleSocialHeaderClick(_eventData) {},

		/**
		 * Handles the social share event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleSocialShare(_eventData) {},

		/**
		 * Handles get directions event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleGetDirections(_eventData) {},

		/**
		 * Handles event data pushed from inside of the promotions tracking js.
		 * The event pushed is called "specialsEvent"
		 * @param {Object} eventData The event data object
		 */
		handleSpecialsEvent(_eventData) {},

		/**
		 * Handles generic widget interaction in the "trackEvent" data format.
		 * These events cover a variety of user actions. The specific widget
		 * interaction should be parsed and handled in each event handler
		 * implementation.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleWidgetUserEvent(_eventData) {},

		/**
		 * Handles widget interaction even when certain params are not available.
		 * This prototype is specifically for use with Adobe DTM Handler.
		 * The handler logic will live in Adobe DTM Handler, but the
		 * handler needs to fire in interaction listener.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleAllWidgetUserEvents(_eventData) {},

		/**
		 * Handles third party user events.
		 * These events are designed to come from third party iframes.
		 * DDC will either transform the iframe message into a third party user event or the third party sends us
		 * the event payload formatted exactly according to the spec:
		 * https://coxautoinc.sharepoint.com/:w:/s/DDCConsumerUX/ER8CjAmgFJFMn4pKb39gIa4BsTvoHpORnnVlDuEQw6Eb0A?e=zRQcEV
		 *
		 * @param {Object} eventData The event data object
		 */
		handleThirdPartyUserEvent(_eventData) {},

		/**
		 * Handles the vehicle compare check event.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleVehicleCompareCheck(_eventData) {},

		/**
		 * Handles clicks on video play button.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleVideoPlay(_eventData) {},

		/**
		 * Handles video milestones.
		 *
		 * @param {Object} eventData The event data object
		 */
		handleVideoMilestone(_eventData) {},

		/**
		 * Handles clicking on slideshow pager pip/dot.
		 *
		 * @param {Object} eventData
		 */
		handleSlideshowPagerClick(_eventData) {}
	};
};

export default runEventHandler;
